import { isEmbedded } from "@/remote/contract";

/**
 * Host mount prefix for this remote's assets. Hard-coded: the shell always
 * mounts this app at `/thq` and serves its bundle + `/assets/*` under it.
 */
export const EMBEDDED_ASSET_PREFIX = "/thq";

/**
 * Root-relative (or absolute) href for a bundled asset, correct in every runtime.
 *
 * Pass the `.href` of a `new URL(asset, import.meta.url)` so Vite's content hash
 * is preserved. That URL resolves the asset against wherever the remote's bundle
 * is actually served from, which differs by runtime:
 *
 * - **Standalone / deployed embedded** — same origin as the page. Standalone
 *   serves `/assets/*` at the root, so the bare pathname is right. Embedded, the
 *   remote is mounted under the host's `/thq/` prefix, but Vite's `base: "/"`
 *   emits a ROOT-absolute path that drops the prefix — so `/assets/x` 404s and
 *   must become `/thq/assets/x`. (This is the reported bug.)
 * - **Local dev embedded** — the Module Federation remote is its OWN dev server
 *   on a DIFFERENT origin than the host shell (the shell keeps :5175, the remote
 *   moves via DEV_PORT). The resolved URL already targets that origin and works
 *   verbatim; a `/thq`-prefixed, host-relative path would wrongly hit the shell.
 *
 * So: cross-origin → use the URL as-is; same-origin → the pathname, prefixed
 * with the mount only when embedded.
 */
export function assetHref(bundledHref: string): string {
  const url = new URL(bundledHref, globalThis.location.href);

  // Cross-origin: the remote serves its own assets (dev). Nothing to rewrite.
  if (url.origin !== globalThis.location.origin) return url.href;

  return isEmbedded()
    ? `${EMBEDDED_ASSET_PREFIX}${url.pathname}`
    : url.pathname;
}
